import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaFileExcel, FaEdit, FaEye, FaTrashAlt } from 'react-icons/fa';
import axios from 'axios';

/**
 * EtikettendruckPage Komponente
 * 
 * Zeigt eine Übersicht aller für den Etikettendruck markierten Artikel und 
 * ermöglicht den Export dieser Artikel als Excel-Datei.
 */
const EtikettendruckPage = () => {
  const [artikel, setArtikel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');

  // Artikel für Etikettendruck laden
  useEffect(() => {
    const fetchArtikel = async () => {
      try {
        setLoading(true);
        
        // Hier verwenden wir den regulären Artikel-Endpunkt mit einem speziellen Query-Parameter
        // für Etikettendruck anstatt des problematischen /api/artikel/etikettendruck Pfads
        const response = await axios.get('/api/artikel', { 
          params: { fuerEtikettendruck: true } 
        });
        
        // Stellen wir sicher, dass wir nur Artikel mit fuerEtikettendruck = 1 oder true anzeigen
        // Dies ist eine doppelte Absicherung, falls der Server-Filter nicht korrekt funktioniert
        // Da SQLite den Wert als Nummer (1) zurückliefert, müssen wir Boolean() oder eine nicht-strikte Prüfung verwenden
        const etikettenArtikel = Array.isArray(response.data) ? 
          response.data.filter(artikel => Boolean(artikel.fuerEtikettendruck)) : 
          [];
        
        console.log('Geladene Etikettendruck-Artikel:', etikettenArtikel.length);
        
        setArtikel(etikettenArtikel);
        setError(null);
      } catch (err) {
        console.error('Fehler beim Laden der Etikettendruck-Artikel:', err);
        setError('Die Artikel für den Etikettendruck konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchArtikel();
  }, []);

  // Artikel aus der Etikettendruckliste entfernen
  const handleRemoveFromPrintList = async (id) => {
    try {
      setLoading(true);
      
      // Bestätigung vom Benutzer einholen
      if (!window.confirm('Soll dieser Artikel wirklich aus der Etikettendruckliste entfernt werden?')) {
        setLoading(false);
        return;
      }

      console.log(`Entferne Artikel ${id} aus der Etikettendruckliste...`);
      
      // Setze fuerEtikettendruck auf false - sende nur dieses Feld
      const response = await axios.put(`/api/artikel/${id}`, { fuerEtikettendruck: false });
      console.log('API-Antwort:', response.data);
      
      // Artikel aus der lokalen Liste entfernen
      setArtikel(prevArtikel => prevArtikel.filter(item => item.id !== id));
      
      setLoading(false);
      setInfoMessage(`Artikel wurde erfolgreich von der Etikettendruckliste entfernt.`);
      
      // Kurze Anzeige der Erfolgsmeldung, dann ausblenden
      setTimeout(() => {
        setInfoMessage('');
      }, 3000);
      
    } catch (err) {
      console.error('Fehler beim Entfernen des Artikels aus der Etikettendruckliste:', err);
      setError(`Fehler beim Entfernen: ${err.response?.data?.message || err.message}`);
      setLoading(false);
    }
  };


  // Excel-Datei herunterladen
  const handleExportExcel = async () => {
    try {
      // Wir erstellen einen Blob mit den Etikettendruck-Artikeln und bieten ihn als Download an
      // Dies ist ein clientseitiger Workaround, falls die direkte Export-URL Probleme bereitet
      const response = await axios.get('/api/artikel/etikettendruck/export', {
        responseType: 'blob' // Wichtig für den Download der Excel-Datei
      });
      
      // Blob-URL erstellen und Download auslösen
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'etikettendruck.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Fehler beim Excel-Export:', err);
      alert('Der Excel-Export konnte nicht durchgeführt werden. Bitte versuchen Sie es später erneut.');
    }
  };

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Etikettendruck</h1>
        <Button
          variant="success"
          onClick={handleExportExcel}
          disabled={loading || artikel.length === 0}
        >
          <FaFileExcel className="me-2" /> Excel exportieren
        </Button>
      </div>

      {infoMessage && (
        <Alert variant="success" dismissible onClose={() => setInfoMessage('')}>
          {infoMessage}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Laden...</span>
          </Spinner>
          <p className="mt-2">Artikel werden geladen...</p>
        </div>
      ) : artikel.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <h4>Keine Artikel für den Etikettendruck markiert</h4>
            <p className="text-muted mb-4">
              Um Artikel für den Etikettendruck zu markieren, bearbeiten Sie einen Artikel und aktivieren Sie die 
              entsprechende Checkbox "Zu Etikettendruck hinzufügen".
            </p>
            <Button as={Link} to="/artikel" variant="primary">
              Zur Artikelübersicht
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Card.Body>
            <p className="text-muted mb-3">
              {artikel.length} {artikel.length === 1 ? 'Artikel' : 'Artikel'} für den Etikettendruck markiert.
            </p>
            <Table responsive hover className="align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Kategorie</th>
                  <th>Name</th>
                  <th>Artikelnummer</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {artikel.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.kategorie}</td>
                    <td>{item.name}</td>
                    <td>{item.artikelnummer || '–'}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button 
                          as={Link} 
                          to={`/artikel/${item.id}`} 
                          variant="outline-primary"
                          size="sm"
                          title="Artikel ansehen"
                        >
                          <FaEye />
                        </Button>
                        <Button 
                          as={Link} 
                          to={`/artikel/${item.id}/edit`} 
                          variant="outline-secondary"
                          size="sm"
                          title="Artikel bearbeiten"
                        >
                          <FaEdit />
                        </Button>
                        <Button 
                          variant="outline-danger"
                          size="sm"
                          title="Aus Etikettendruckliste entfernen"
                          onClick={() => handleRemoveFromPrintList(item.id)}
                        >
                          <FaTrashAlt />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default EtikettendruckPage;
